import { useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { X, Camera, MapPin, Headphones, ChartBar, XCircle, MusicNotes, UserCircle, Lightbulb } from 'phosphor-react';
import { theme } from '../../styles/theme';
import { useTranslation } from '../../i18n';

interface ActionSheetProps {
  isOpen: boolean;
  onClose: () => void;
  onActionSelect: (action: ActionType) => void;
  mode?: 'pre-chat' | 'in-chat';
  messageCount?: number;
}

const MIN_MESSAGES_FOR_ACTIONS = 4;
const ACTIONS_REQUIRING_MESSAGES: ActionType[] = ['suggest-content', 'talk-to-expert', 'generate-insights'];

export type ActionType =
  // Pre-chat actions
  | 'capture-food' | 'find-gym' | 'content-suggestion' | 'nutrition-survey'
  // In-chat actions
  | 'end-conversation' | 'suggest-content' | 'talk-to-expert' | 'generate-insights';


const slideUp = keyframes`
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(0);
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const Overlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: ${theme.zIndex.modal};
  display: ${({ $isOpen }) => ($isOpen ? 'flex' : 'none')};
  align-items: flex-end;
  animation: ${fadeIn} 0.3s ease-out;
`;

const Sheet = styled.div`
  width: 100%;
  max-width: 375px;
  margin: 0 auto;
  background: var(--color-bg);
  border-radius: 24px 24px 0 0;
  padding: 24px;
  padding-bottom: max(24px, env(safe-area-inset-bottom));
  animation: ${slideUp} 0.3s ease-out;
  position: relative;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
`;

const Title = styled.h2`
  font-family: ${theme.typography.fontFamily.primary};
  font-size: 18px;
  font-weight: 600;
  color: var(--color-text);
  margin: 0;
`;

const CloseButton = styled.button`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--color-bg-elevated);
  border-radius: ${theme.borderRadius.full};
  color: var(--color-text);
  transition: all ${theme.transitions.fast};

  &:hover {
    background: var(--color-border-subtle);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const ActionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
`;

const ActionButton = styled.button<{ $disabled?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px 16px;
  background: var(--color-bg-elevated);
  border: 1px solid var(--color-border-subtle);
  border-radius: 16px;
  transition: all ${theme.transitions.fast};
  min-height: 120px;
  opacity: ${({ $disabled }) => ($disabled ? 0.45 : 1)};
  cursor: ${({ $disabled }) => ($disabled ? 'default' : 'pointer')};

  &:hover {
    background: var(--color-bg-elevated);
    border-color: ${({ $disabled }) => ($disabled ? 'var(--color-border-subtle)' : 'var(--color-border)')};
    transform: ${({ $disabled }) => ($disabled ? 'none' : 'translateY(-2px)')};
  }

  &:active {
    transform: ${({ $disabled }) => ($disabled ? 'none' : 'translateY(0)')};
  }
`;

const DisabledHint = styled.span`
  font-family: ${theme.typography.fontFamily.secondary};
  font-size: 10px;
  color: var(--color-text-muted);
  text-align: center;
  margin-top: 6px;
  line-height: 1.3;
`;

const IconWrapper = styled.div`
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--color-bg-elevated);
  border-radius: ${theme.borderRadius.full};
  margin-bottom: 12px;
  color: var(--color-text);
`;

const ActionLabel = styled.div`
  font-family: ${theme.typography.fontFamily.secondary};
  font-size: 13px;
  font-weight: 500;
  color: var(--color-text);
  text-align: center;
  line-height: 1.4;
`;

const ACTION_ICONS: Record<ActionType, React.ReactNode> = {
  // Pre-chat
  'capture-food': <Camera size={24} weight="regular" />,
  'find-gym': <MapPin size={24} weight="regular" />,
  'content-suggestion': <Headphones size={24} weight="regular" />,
  'nutrition-survey': <ChartBar size={24} weight="regular" />,
  // In-chat
  'end-conversation': <XCircle size={24} weight="regular" />,
  'suggest-content': <MusicNotes size={24} weight="regular" />,
  'talk-to-expert': <UserCircle size={24} weight="regular" />,
  'generate-insights': <Lightbulb size={24} weight="regular" />,
};

const PRE_CHAT_ACTIONS: Array<{ id: ActionType; labelKey: string }> = [
  { id: 'capture-food', labelKey: 'actions.captureFood' },
  { id: 'find-gym', labelKey: 'actions.findGym' },
  { id: 'content-suggestion', labelKey: 'actions.contentSuggestion' },
  { id: 'nutrition-survey', labelKey: 'actions.nutritionSurvey' },
];

const IN_CHAT_ACTIONS: Array<{ id: ActionType; labelKey: string }> = [
  { id: 'end-conversation', labelKey: 'chatActions.endConversation' },
  { id: 'suggest-content', labelKey: 'chatActions.suggestContent' },
  { id: 'talk-to-expert', labelKey: 'chatActions.talkToExpert' },
  { id: 'generate-insights', labelKey: 'chatActions.generateInsights' },
];

export const ActionSheet: React.FC<ActionSheetProps> = ({ isOpen, onClose, onActionSelect, mode = 'pre-chat', messageCount = 0 }) => {
  const { t, lang } = useTranslation();
  const sheetRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const actions = mode === 'in-chat' ? IN_CHAT_ACTIONS : PRE_CHAT_ACTIONS;
  const title = mode === 'in-chat' ? t('chatActions.title') : t('actions.title');
  const hasEnoughMessages = messageCount >= MIN_MESSAGES_FOR_ACTIONS;

  // Prevent body scroll when sheet is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Close on escape key (listener only attached while open)
  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Move focus into the dialog on open, restore it on close/unmount
  useEffect(() => {
    if (!isOpen) return;
    previousFocusRef.current = document.activeElement as HTMLElement | null;
    sheetRef.current?.focus();
    return () => {
      previousFocusRef.current?.focus();
      previousFocusRef.current = null;
    };
  }, [isOpen]);

  const handleActionClick = (actionId: ActionType) => {
    onActionSelect(actionId);
    onClose();
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <Overlay $isOpen={isOpen} onClick={handleOverlayClick}>
      <Sheet
        ref={sheetRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="action-sheet-title"
        tabIndex={-1}
      >
        <Header>
          <Title id="action-sheet-title">{title}</Title>
          <CloseButton onClick={onClose} aria-label="Close">
            <X size={20} weight="bold" />
          </CloseButton>
        </Header>

        <ActionsGrid>
          {actions.map((action) => {
            const needsMessages = ACTIONS_REQUIRING_MESSAGES.includes(action.id);
            const isDisabled = needsMessages && !hasEnoughMessages;
            return (
              <ActionButton
                key={action.id}
                $disabled={isDisabled}
                onClick={() => !isDisabled && handleActionClick(action.id)}
                aria-label={t(action.labelKey)}
                aria-disabled={isDisabled}
              >
                <IconWrapper>{ACTION_ICONS[action.id]}</IconWrapper>
                <ActionLabel>{t(action.labelKey)}</ActionLabel>
                {isDisabled && (
                  <DisabledHint>
                    {lang === 'tr' ? 'Yeterli konuşma detayına ulaşılmadı' : 'Not enough conversation detail'}
                  </DisabledHint>
                )}
              </ActionButton>
            );
          })}
        </ActionsGrid>
      </Sheet>
    </Overlay>
  );
};
